import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "../styles/globals.css";
import type { AppContext, AppProps } from "next/app";
import { useRouter } from "next/router";
import { i18nCreator as createSundayComponentV2I18n } from "sunday-component";
import { i18nCreator as createSundayComponentV3I18n } from "@sunday/component";
import { appWithTranslation, SSRConfig, useTranslation } from "next-i18next";
import { ThemeProvider, DefaultTheme } from "styled-components";
import React from "react";
import "antd/dist/antd.min.css";
import "sunday-component/lib/styles.css";
import "dayjs/locale/th";
import Script from "next/script";
import App from "next/app";
import GlobalThemeStyles from "src/styles/globalThemeStyle";
import getChannelConfigByPathName from "src/api/channel";
import { ChannelConfigType } from "src/types/channelConfig";
import { GTM_AUTH, GTM_ID, GTM_PREVIEW } from "src/constants/gtm";
import { logError } from "src/utils/logger";
import { QueryClientProvider } from "react-query";
import { queryClient } from "src/api/queryClient";
import Head from "next/head";
import { ParallaxProvider } from "react-scroll-parallax";
import { getPath } from "../utils/router";

export type AppPropType = {
  channelConfig: ChannelConfigType;
};

function MyApp({
  Component,
  pageProps,
}: AppProps<
  {
    theme: DefaultTheme;
    channelConfig: ChannelConfigType;
    title?: string;
    description?: string;
    image?: string;
  } & SSRConfig
>) {
  const router = useRouter();
  const currentLanguage = getPath(router.locale).lang;
  const { t } = useTranslation("common");
  // if you fix re-render the whole app when change-language then use instance form i18nCreator instend
  createSundayComponentV2I18n(currentLanguage);
  createSundayComponentV3I18n(currentLanguage);
  const supportedLangCodes = pageProps?.channelConfig?.content?.supportedLangs
    .map((lang: { code: string }) => lang.code)
    .join("|");

  const generateUrl = (locale) => {
    return `${process.env.NEXT_PUBLIC_BASE_PATH}/${locale}${router.asPath
      .replace(new RegExp(`^/(${supportedLangCodes})/`, "i"), "")
      .replace(/\?.*$/, "")}`;
  };

  const fav = "/static/favicon.png";
  // pageProps?.channelConfig?.content?.webHeader?.icon
  return (
    <>
      <Head>
        <title>{pageProps.title || t("header.title")}</title>
        <link rel="icon" href={fav} />
        <meta
          name="description"
          content={pageProps.description || t("header.description")}
        />
        <meta
          property="og:image"
          content={pageProps.image || "/static/web-image.jpeg"}
        />
        <link rel="canonical" href={generateUrl(currentLanguage)} />
        {pageProps?.channelConfig?.content?.supportedLangs.map(
          (locale: { code: string }) => (
            <link
              rel="alternate"
              href={generateUrl(locale?.code)}
              hrefLang={locale?.code}
            />
          )
        )}
        <link rel="alternate" href={generateUrl("en")} hrefLang="x-default" />
      </Head>
      <Script id="google-tag-manager" strategy="afterInteractive">
        {`
          (function(w,d,s,l,i,x,y){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl+'&gtm_auth='+x+'&gtm_preview='+y;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${GTM_ID}','${GTM_AUTH}','${GTM_PREVIEW}');
      `}
      </Script>
      <ParallaxProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={pageProps.theme}>
            <GlobalThemeStyles />
            <Component {...pageProps} channelConfig={pageProps.channelConfig} />
          </ThemeProvider>
        </QueryClientProvider>
      </ParallaxProvider>
    </>
  );
}

MyApp.getInitialProps = async (appContext: AppContext) => {
  // calls page's `getInitialProps` and fills `appProps.pageProps`
  const appProps = await App.getInitialProps(appContext);
  const { locale } = appContext.router;
  const theme: DefaultTheme = {
    breakpoints: {
      xs: "480px",
      sm: "576px",
      md: "768px",
      lg: "992px",
      xl: "1200px",
      xxl: "1600px",
    },
    themeConfig: null,
  };
  try {
    let isDuplicateQuery = false;
    const queryObj = {};

    const query = new URLSearchParams(
      appContext.router.asPath.split("?")?.[1] || ""
    );

    Array.from(query.keys()).forEach((key) => {
      const searchParamsValue = query.getAll(key);
      if (searchParamsValue.length > 1) {
        isDuplicateQuery = true;
      }
      [queryObj[key]] = searchParamsValue;
    });

    // DEV-NOTE: should move to nextjs middleware
    // (wait @sentry/nextjs (v7.41.0) stable for nextjs middleware)
    // remove duplicate query string (not work in getServerSideProps)
    if (isDuplicateQuery) {
      // send error to sentry.
      logError("Error duplicate query string", {
        query: appContext.router.query,
        path: appContext.router.asPath,
      });
      const querystring = new URLSearchParams(queryObj).toString();
      appContext.ctx.res.writeHead(307, {
        Location: `${appContext.router.asPath.split("?")?.[0]}?${querystring}`,
      });
      appContext.ctx.res.end();
    }

    const { channelPath } = appContext.router.query;
    const path = appContext.router.asPath.split("/");
    if (channelPath) {
      const channelConfig = await getChannelConfigByPathName(
        channelPath as string
      );

      theme.themeConfig = channelConfig.theme;

      appProps.pageProps = {
        ...appProps.pageProps,
        title:
          appProps.pageProps.title ||
          channelConfig?.content?.webHeader?.title?.[locale] ||
          null,
        description:
          appProps.pageProps.description ||
          channelConfig?.content?.webHeader?.description?.[locale] ||
          null,
        image:
          appProps.pageProps.image ||
          channelConfig?.content?.webHeader?.image ||
          null,
        theme,
        channelConfig,
      };
      return appProps;
    }
    if (path.length > 1) {
      const channelConfig = await getChannelConfigByPathName(path[1]);

      theme.themeConfig = channelConfig.theme;

      appProps.pageProps = {
        ...appProps.pageProps,
        title:
          appProps.pageProps.title ||
          channelConfig?.content?.webHeader?.title?.[locale] ||
          null,
        description:
          appProps.pageProps.description ||
          channelConfig?.content?.webHeader?.description?.[locale] ||
          null,
        image:
          appProps.pageProps.image ||
          channelConfig?.content?.webHeader?.image ||
          null,
        theme,
        channelConfig,
      };
      return appProps;
    }
    appProps.pageProps = {
      ...appProps.pageProps,
      theme,
      channelConfig: { content: null },
    };
    return appProps;
  } catch (error) {
    appProps.pageProps = {
      ...appProps.pageProps,
      theme,
      channelConfig: { content: null },
    };
    return appProps;
  }
};

export default appWithTranslation(MyApp);
